import { useEffect, useMemo, useState } from 'react';

import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Permission } from '../../../../../../../backend/src/shared/action-registry';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useSubmitMatterIssues } from '@/api/mutations/useSubmitMatterIssues';
import { useSyncMatterParties } from '@/api/mutations/useSyncMatterParties';
import { useSyncSubsetIssues } from '@/api/mutations/useSyncSubsetIssues';
import { useUpdateMatter } from '@/api/mutations/useUpdateMatter';
import { useGetMatterParties } from '@/api/queries/useGetMatterParties';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { useGetSubsetIssues } from '@/api/queries/useGetSubsetIssues';
import { QueryEntity } from '@/api/types';
import { useUserContext } from '@/Contexts/User';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { InfoCard } from '@/new/components/info-card';
import { IssuesList, type IssueFormItem } from '@/new/components/matter/issues-list';
import { LegacyContextDialog, hasLegacyContext } from '@/new/components/matter/legacy-context-dialog';
import { PartiesList, type PartyFormItem } from '@/new/components/matter/parties-list';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Textarea } from '@/new/components/shadcn/textarea';

export function Issues() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const queryClient = useQueryClient();
  const { user } = useUserContext();
  const { data: matter } = useGetMatterV2(matterId);
  const isCreator = matter?.createdById === user?.id;

  const allDocumentsCaseId = matter?.allDocumentsCaseId ?? undefined;
  const { data: serverIssues = [] } = useGetSubsetIssues(matterId, allDocumentsCaseId);
  const { data: serverParties = [] } = useGetMatterParties(matterId);
  const { mutateAsync: updateMatter } = useUpdateMatter(matterId);
  const { mutateAsync: submitIssues, isLoading: isSubmittingIssues } = useSubmitMatterIssues(matterId);
  const syncIssues = useSyncSubsetIssues(matterId, allDocumentsCaseId);
  const syncParties = useSyncMatterParties(matterId);

  const { data: allDocsSubset } = useGetSubset(matterId, matter?.allDocumentsCaseId ?? undefined);
  const { data: allDocsCase } = useGetSubset(matterId, allDocsSubset?.id);

  const hasLegacy = hasLegacyContext(allDocsCase);

  // Local state for issues and parties
  const [localIssues, setLocalIssues] = useState<IssueFormItem[]>([]);
  const [localParties, setLocalParties] = useState<PartyFormItem[]>([]);
  const [issuesSeeded, setIssuesSeeded] = useState(false);
  const [partiesSeeded, setPartiesSeeded] = useState(false);

  // Seed local state from server data
  useEffect(() => {
    if (serverIssues.length > 0 && !issuesSeeded) {
      setLocalIssues(serverIssues.map((i) => ({ id: i.id, label: i.label, text: i.text })));
      setIssuesSeeded(true);
    }
  }, [serverIssues, issuesSeeded]);

  useEffect(() => {
    if (serverParties.length > 0 && !partiesSeeded) {
      setLocalParties(
        serverParties.map((p) => ({
          id: p.id,
          name: p.name,
          isClient: p.isClient,
          role: p.role ?? 'other',
          type: p.type === 'place' ? null : p.type,
        })),
      );
      setPartiesSeeded(true);
    }
  }, [serverParties, partiesSeeded]);

  const [additionalContext, setAdditionalContext] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showLegacyContext, setShowLegacyContext] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const hasDocuments = (allDocsSubset?.docCount ?? 0) > 0;

  const hasUnsavedChanges = useMemo(() => {
    // Issues changed
    const issuesChanged =
      localIssues.length !== serverIssues.length ||
      localIssues.some(
        (li, i) =>
          li.id !== serverIssues[i]?.id || li.label !== serverIssues[i]?.label || li.text !== serverIssues[i]?.text,
      );

    // Parties changed
    const partiesChanged =
      localParties.length !== serverParties.length ||
      localParties.some(
        (lp, i) =>
          lp.id !== serverParties[i]?.id ||
          lp.name !== serverParties[i]?.name ||
          lp.isClient !== serverParties[i]?.isClient ||
          lp.role !== (serverParties[i]?.role ?? 'other') ||
          (lp.type ?? null) !== (serverParties[i]?.type ?? null),
      );

    // Context changed
    const contextChanged = additionalContext !== (matter?.additionalContext ?? '');

    return issuesChanged || partiesChanged || contextChanged;
  }, [localIssues, localParties, additionalContext, serverIssues, serverParties, matter?.additionalContext]);

  useEffect(() => {
    if (matter?.additionalContext != null) {
      setAdditionalContext(matter.additionalContext);
    }
  }, [matter?.additionalContext]);

  const handleSave = async () => {
    if (!allDocumentsCaseId) return;
    setIsSaving(true);
    try {
      await Promise.all([
        syncIssues.mutateAsync(localIssues),
        syncParties.mutateAsync(localParties),
        updateMatter({ additionalContext }),
      ]);
      // Refresh server state so hasUnsavedChanges recomputes and pick up generated IDs
      await Promise.all([
        queryClient.invalidateQueries([QueryEntity.SubsetIssues, { matterId, subsetId: allDocumentsCaseId }]),
        queryClient.invalidateQueries([QueryEntity.MatterParties, { matterId }]),
        queryClient.invalidateQueries([QueryEntity.Matter, { matterId }]),
      ]);
      setIssuesSeeded(false);
      setPartiesSeeded(false);
      trackCustomEvent(TrackingEventsV2.MatterIssuesSave, {
        matterId,
        issueCount: localIssues.length,
        partyCount: localParties.length,
      });
      toast.success('Changes saved.');
    } catch {
      toast.error('Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReprocess = async () => {
    if (!allDocumentsCaseId) return;
    try {
      // Save first, then reprocess
      await Promise.all([
        syncIssues.mutateAsync(localIssues),
        syncParties.mutateAsync(localParties),
        updateMatter({ additionalContext }),
      ]);
      await submitIssues();
      trackCustomEvent(TrackingEventsV2.MatterIssuesSubmit, {
        matterId,
        issueCount: localIssues.length,
        partyCount: localParties.length,
      });
      setShowSubmitModal(false);
      setPartiesSeeded(false);
      setIssuesSeeded(false);
      toast.success('Changes saved. Synopsis update started.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to submit issues.');
    }
  };

  return (
    <PermissionGate permission={Permission.MatterEdit} orAllowed={isCreator} uiMode="disabled">
      <div className="max-w-5xl pb-12">
        <div className="mb-4 flex items-start justify-between gap-2">
          {matter && allDocsSubset && !hasDocuments && (
            <InfoCard className="mt-1 mb-4">Add documents before updating Matter issues.</InfoCard>
          )}
          <div className="flex items-center gap-2">
            <p className="w-fit items-center gap-2.5 font-serif text-lg font-medium">Matter Context</p>
            {hasUnsavedChanges && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">Unsaved changes</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" disabled={isSaving || !hasDocuments || !hasUnsavedChanges} onClick={handleSave}>
              {isSaving ? (
                <>
                  Saving...
                  <Icon className="lucide--loader-circle h-3.5 w-3.5 animate-spin duration-700" />
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
            <Button
              variant="primary"
              disabled={localIssues.length === 0 || isSubmittingIssues || !hasDocuments}
              onClick={() => setShowSubmitModal(true)}
            >
              Reprocess
              <Icon className="lucide--arrow-right h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {hasLegacy && (
          <p className="mb-6 flex w-fit items-start gap-1 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-600">
            This matter has context in the legacy format.{' '}
            <button
              type="button"
              onClick={() => setShowLegacyContext(true)}
              className="font-medium text-amber-600 underline hover:text-amber-700"
            >
              View old context
            </button>
          </p>
        )}

        <div className="space-y-12">
          <section>
            <h3 className="mb-1 text-sm font-medium">Issues</h3>
            <p className="mb-4 text-xs text-gray-500">
              Issues help Wexler determine relevant facts extracted from your documents.
            </p>
            <IssuesList issues={localIssues} onChange={setLocalIssues} />
          </section>

          <hr />

          <section>
            <h3 className="mb-1 text-sm font-medium">Parties</h3>
            <p className="mb-4 text-xs text-gray-500">
              Parties help Wexler identify relevant individuals and organisations across your documents.
            </p>
            <PartiesList parties={localParties} onChange={setLocalParties} />
          </section>

          <hr />

          <section>
            <h3 className="mb-1 text-sm font-medium">Additional Context</h3>
            <p className="mb-4 text-xs text-gray-500">
              Any additional information about your matter that may help Wexler determine the relevance of facts.
            </p>
            <Textarea
              className="min-h-36"
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
            />
          </section>
        </div>
      </div>

      <Dialog open={showSubmitModal} onOpenChange={setShowSubmitModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reprocess Issues & Context</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p>
              This will save your changes and re-tag the relevance of all facts and generate a new summary based on this
              new information.
            </p>
            <p className="text-xs text-gray-500">
              This process may take some time depending on the number of facts in your matter.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleReprocess} disabled={isSubmittingIssues}>
              {isSubmittingIssues ? (
                <>
                  Processing...
                  <Icon className="lucide--loader-circle h-3.5 w-3.5 animate-spin duration-700" />
                </>
              ) : (
                <>
                  Confirm
                  <Icon className="lucide--arrow-right h-3.5 w-3.5" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LegacyContextDialog
        open={showLegacyContext}
        onOpenChange={setShowLegacyContext}
        title="Previous Matter Context"
        data={allDocsCase}
      />
    </PermissionGate>
  );
}
